import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { RelatedNews } from "@/components/RelatedNews";
import { API } from "@/App";
import { Trophy, Calendar, ArrowLeft, ChevronRight } from "lucide-react";
import { competitionLogo } from "@/lib/competitionLogo";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatMatchDate } from "@/lib/dates";
import BracketView from "@/components/BracketView";

const CompetitionDetailPage = () => {
  const { competitionId } = useParams();
  const [competition, setCompetition] = useState(null);
  const [standings, setStandings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [compRes, standingsRes, matchesRes, teamsRes, scorersRes] = await Promise.all([
          axios.get(`${API}/competitions/${competitionId}`),
          axios.get(`${API}/standings/${competitionId}`),
          axios.get(`${API}/matches?competition_id=${competitionId}`),
          axios.get(`${API}/teams`),
          axios.get(`${API}/top-scorers?competition_id=${competitionId}&limit=10`)
        ]);

        setCompetition(compRes.data);
        setStandings(standingsRes.data);
        setMatches(matchesRes.data);
        setTopScorers(scorersRes.data);

        // Create teams map
        const tMap = {};
        teamsRes.data.forEach(t => { tMap[t.team_id] = t; });
        setTeams(tMap);
      } catch (error) {
        console.error("Error fetching competition data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [competitionId]);

  const formatDate = (dateStr) => formatMatchDate(dateStr, { weekday: 'short', month: 'short', day: 'numeric' });

  // Sort: fixtures earliest first, results latest first (stable by match_time + match_id)
  const _cmp = (a, b) => {
    const d = (a.match_date || "").localeCompare(b.match_date || "");
    if (d !== 0) return d;
    return (a.match_time || "").localeCompare(b.match_time || "");
  };
  const fixtures = matches.filter(m => m.status === "scheduled").sort(_cmp);
  const results = matches.filter(m => m.status === "completed").sort((a, b) => -_cmp(a, b));

  if (loading) {
    return (
      <div className="min-h-screen py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <Skeleton className="h-64 w-full mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-400">Competition Not Found</h2>
          <Link to="/competitions" className="text-[#009E60] hover:underline mt-4 inline-block">
            Back to Competitions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="competition-detail-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Link to="/competitions" className="inline-flex items-center text-gray-400 hover:text-white mb-6" data-testid="back-to-competitions">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Competitions
          </Link>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-32 bg-gray-800 flex items-center justify-center p-2">
              {competitionLogo(competition)
                ? <img src={competitionLogo(competition)} alt={`${competition.name} logo`} className="max-w-full max-h-full object-contain" data-testid="competition-header-logo" />
                : <Trophy className="w-16 h-16 text-[#FFC72C]" />}
            </div>
            <div className="text-center md:text-left">
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase text-white mb-2">
                {competition.name}
              </h1>
              <div className="flex items-center gap-4 text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Season {competition.season}
                </span>
                <span className="capitalize px-3 py-1 bg-[#009E60] text-white text-sm font-bold">
                  {competition.status}
                </span>
              </div>
              {competition.description && (
                <p className="text-gray-400 mt-4 max-w-xl">{competition.description}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue={competition?.type === "cup" ? "bracket" : "standings"} className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0">
              {(competition?.type === "cup"
                ? ["bracket", "fixtures", "results", "top-scorers"]
                : ["standings", "fixtures", "results", "top-scorers"]
              ).map(tab => (
                <TabsTrigger 
                  key={tab}
                  value={tab}
                  className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent"
                  data-testid={`tab-${tab}`}
                >
                  {tab.replace('-', ' ')}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Bracket Tab (cup only) */}
            {competition?.type === "cup" && (
              <TabsContent value="bracket">
                <BracketView matches={matches} teams={teams} />
              </TabsContent>
            )}

            {/* Standings Tab */}
            <TabsContent value="standings">
              {(() => {
                const hasAnyPlayed = standings.some((s) => (s.played || 0) > 0);
                if (standings.length === 0) {
                  return (
                    <div className="text-center py-12" data-testid="standings-none">
                      <p className="text-gray-500">No teams registered in this competition yet.</p>
                    </div>
                  );
                }
                if (!hasAnyPlayed) {
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg py-16 px-6 text-center" data-testid="standings-not-started">
                      <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="font-heading text-lg font-bold text-[#09090b] mb-1 uppercase">
                        {competition?.season || "Season"} Not Yet Underway
                      </h3>
                      <p className="text-gray-500 text-sm max-w-md mx-auto">
                        The league table will appear here once matches have been played. Head to the Fixtures tab to see what's coming up.
                      </p>
                    </div>
                  );
                }
                return (
                <div className="overflow-x-auto">
                  <table className="table-abfa" data-testid="standings-table">
                    <thead>
                      <tr>
                        <th className="w-12">#</th>
                        <th>Team</th>
                        <th className="text-center w-12">P</th>
                        <th className="text-center w-12">W</th>
                        <th className="text-center w-12">D</th>
                        <th className="text-center w-12">L</th>
                        <th className="text-center w-12">GF</th>
                        <th className="text-center w-12">GA</th>
                        <th className="text-center w-12">GD</th>
                        <th className="text-center w-16">Pts</th>
                        <th className="w-32">Form</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((s, i) => (
                        <tr key={s.team_id} className={i < 3 ? 'bg-green-50' : ''} data-testid={`standing-row-${i}`}>
                          <td className="font-bold">{s.position}</td>
                          <td>
                            <Link to={`/teams/${s.team_id}`} className="flex items-center gap-3 hover:text-[#009E60]">
                              <img 
                                src={teams[s.team_id]?.logo_url || 'https://placehold.co/32x32/09090b/FFF?text=T'} 
                                alt="" 
                                className="w-8 h-8 object-contain"
                              />
                              <span className="font-medium">{teams[s.team_id]?.name || 'Unknown'}</span>
                            </Link>
                          </td>
                          <td className="text-center">{s.played}</td>
                          <td className="text-center">{s.won}</td>
                          <td className="text-center">{s.drawn}</td>
                          <td className="text-center">{s.lost}</td>
                          <td className="text-center">{s.goals_for}</td>
                          <td className="text-center">{s.goals_against}</td>
                          <td className="text-center font-bold">{s.goal_difference > 0 ? `+${s.goal_difference}` : s.goal_difference}</td>
                          <td className="text-center font-bold text-lg">{s.points}</td>
                          <td>
                            <div className="flex gap-1">
                              {(s.form || []).map((result, j) => (
                                <span 
                                  key={j} 
                                  className={`form-badge form-badge-${result.toLowerCase()}`}
                                >
                                  {result}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                );
              })()}
            </TabsContent>

            {/* Fixtures Tab */}
            <TabsContent value="fixtures">
              {fixtures.length > 0 ? (
                <div className="space-y-4">
                  {fixtures.map(match => (
                    <Link
                      key={match.match_id}
                      to={`/matches/${match.match_id}`}
                      className="match-card hover:bg-gray-50 transition-colors"
                      data-testid={`fixture-card-${match.match_id}`}
                    >
                      <div className="text-sm text-gray-500 mb-2 md:mb-0 md:w-32">
                        <p className="font-medium">{formatDate(match.match_date)}</p>
                        <p>{match.match_time || 'TBD'}</p>
                      </div>
                      <div className="flex items-center gap-4 flex-1 justify-center">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-lg">{teams[match.home_team_id]?.short_name || 'HOME'}</span>
                          <img
                            src={teams[match.home_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=H'}
                            alt=""
                            className="w-12 h-12 object-contain"
                          />
                        </div>
                        <span className="text-gray-400 font-heading text-xl">VS</span>
                        <div className="flex items-center gap-2">
                          <img
                            src={teams[match.away_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=A'}
                            alt=""
                            className="w-12 h-12 object-contain"
                          />
                          <span className="font-heading font-bold text-lg">{teams[match.away_team_id]?.short_name || 'AWAY'}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 text-right mt-2 md:mt-0 md:w-32">
                        <p>{match.stadium || 'TBD'}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No upcoming fixtures.</p>
                </div>
              )}
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results">
              {results.length > 0 ? (
                <div className="space-y-4">
                  {results.map(match => (
                    <Link
                      key={match.match_id}
                      to={`/matches/${match.match_id}`}
                      className="match-card border-l-[#009E60] hover:bg-gray-50 transition-colors"
                      data-testid={`result-card-${match.match_id}`}
                    >
                      <div className="text-sm text-gray-500 mb-2 md:mb-0 md:w-32">
                        <p className="font-medium">{formatDate(match.match_date)}</p>
                      </div>
                      <div className="flex items-center gap-4 flex-1 justify-center">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-lg">{teams[match.home_team_id]?.short_name || 'HOME'}</span>
                          <img
                            src={teams[match.home_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=H'}
                            alt=""
                            className="w-12 h-12 object-contain"
                          />
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="flex items-center gap-2 font-heading font-bold text-3xl">
                            <span className={match.home_score > match.away_score ? 'text-[#009E60]' : ''}>{match.home_score}</span>
                            <span className="text-gray-300">-</span>
                            <span className={match.away_score > match.home_score ? 'text-[#009E60]' : ''}>{match.away_score}</span>
                          </div>
                          {match.shootout_home_score != null && match.shootout_away_score != null && (
                            <div className="text-[10px] font-semibold text-[#FFC72C] uppercase tracking-widest mt-0.5" data-testid={`shootout-tag-${match.match_id}`}>
                              ({match.shootout_home_score}-{match.shootout_away_score} pens)
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <img
                            src={teams[match.away_team_id]?.logo_url || 'https://placehold.co/48x48/09090b/FFF?text=A'}
                            alt=""
                            className="w-12 h-12 object-contain"
                          />
                          <span className="font-heading font-bold text-lg">{teams[match.away_team_id]?.short_name || 'AWAY'}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500 text-right mt-2 md:mt-0 md:w-32">
                        <span className="px-2 py-1 bg-gray-100 text-xs uppercase font-bold">FT</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No results yet.</p>
                </div>
              )}
            </TabsContent>

            {/* Top Scorers Tab */}
            <TabsContent value="top-scorers">
              {topScorers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table-abfa" data-testid="top-scorers-table">
                    <thead>
                      <tr>
                        <th className="w-12">#</th>
                        <th>Player</th>
                        <th>Team</th>
                        <th className="text-center w-16">Goals</th>
                        <th className="text-center w-16">Penalties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topScorers.map((scorer, i) => (
                        <tr
                          key={scorer.player_id}
                          data-testid={`scorer-row-${i}`}
                          className="hover:bg-gray-50"
                        >
                          <td className="font-bold">{i + 1}</td>
                          <td>
                            <Link
                              to={`/players/${scorer.player_id}`}
                              className="flex items-center gap-3 hover:text-[#009E60]"
                              data-testid={`scorer-player-link-${scorer.player_id}`}
                            >
                              <img
                                src={scorer.player_photo || 'https://placehold.co/40x40/09090b/FFF?text=P'}
                                alt=""
                                className="w-10 h-10 object-cover rounded-full"
                              />
                              <span className="font-medium">{scorer.player_name}</span>
                            </Link>
                          </td>
                          <td>
                            <Link
                              to={`/teams/${scorer.team_id}`}
                              className="flex items-center gap-2 hover:text-[#009E60]"
                              data-testid={`scorer-team-link-${scorer.team_id}`}
                            >
                              <img
                                src={scorer.team_logo || 'https://placehold.co/24x24/09090b/FFF?text=T'}
                                alt=""
                                className="w-6 h-6 object-contain"
                              />
                              <span>{scorer.team_name}</span>
                            </Link>
                          </td>
                          <td className="text-center font-bold text-lg">{scorer.goals}</td>
                          <td className="text-center text-gray-500">{scorer.penalties}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No goals scored yet.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <RelatedNews entity="competition_id" id={competitionId} title="Competition News" />
    </div>
  );
};

export default CompetitionDetailPage;
